import React, { useState } from 'react';
import { FileText, Download, Search, Filter, CheckCircle, FileSignature, Clock } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { Modal } from '../ui/Modal';
interface Document {
  id: number;
  title: string;
  category: string;
  date: string;
  type: string;
  requiresSignature?: boolean;
  signed?: boolean;
  signedDate?: string;
}
export function Documents() {
  const {
    showToast
  } = useToast();
  const [isSignModalOpen, setIsSignModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  // Mock data
  const documentCategories = [{
    id: 1,
    name: 'Administratif',
    count: 8
  }, {
    id: 2,
    name: 'Avantages sociaux',
    count: 5
  }, {
    id: 3,
    name: 'Politiques RH',
    count: 9
  }, {
    id: 4,
    name: 'Guides & Procédures',
    count: 7
  }];
  const [recentDocuments, setRecentDocuments] = useState<Document[]>([{
    id: 1,
    title: 'Attestation employeur',
    category: 'Administratif',
    date: '15 sept. 2023',
    type: 'pdf',
    requiresSignature: false,
    signed: false
  }, {
    id: 2,
    title: 'Politique de télétravail',
    category: 'Politiques RH',
    date: '1 sept. 2023',
    type: 'pdf',
    requiresSignature: true,
    signed: false
  }, {
    id: 3,
    title: 'Guide des avantages sociaux',
    category: 'Avantages sociaux',
    date: '15 août 2023',
    type: 'pdf',
    requiresSignature: false,
    signed: false
  }, {
    id: 4,
    title: 'Contrat de travail',
    category: 'Administratif',
    date: '1 août 2023',
    type: 'pdf',
    requiresSignature: true,
    signed: true,
    signedDate: '5 août 2023'
  }, {
    id: 5,
    title: 'Politique de sécurité sur les chantiers',
    category: 'Politiques RH',
    date: '15 juil. 2023',
    type: 'pdf',
    requiresSignature: true,
    signed: false
  }, {
    id: 6,
    title: 'Code de conduite',
    category: 'Politiques RH',
    date: '10 juil. 2023',
    type: 'pdf',
    requiresSignature: true,
    signed: false
  }, {
    id: 7,
    title: 'Politique de confidentialité',
    category: 'Administratif',
    date: '5 juil. 2023',
    type: 'pdf',
    requiresSignature: true,
    signed: true,
    signedDate: '8 juil. 2023'
  }]);
  const handleOpenSignModal = (document: Document) => {
    setSelectedDocument(document);
    setIsSignModalOpen(true);
  };
  const handleSignDocument = () => {
    if (!selectedDocument) return;
    // Mettre à jour le document comme signé
    setRecentDocuments(recentDocuments.map(doc => doc.id === selectedDocument.id ? {
      ...doc,
      signed: true,
      signedDate: new Date().toLocaleDateString('fr-CA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    } : doc));
    setIsSignModalOpen(false);
    setSelectedDocument(null);
    showToast('Document signé avec succès', 'success');
  };
  const getUnsignedCount = () => {
    return recentDocuments.filter(doc => doc.requiresSignature && !doc.signed).length;
  };
  return <div className="pb-20 md:pb-0" data-id="element-297">
      <div className="mb-6" data-id="element-298">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-299">Documents RH</h1>
        <p className="text-gray-600" data-id="element-300">
          Accédez à vos documents personnels et aux ressources RH
        </p>
      </div>

      {/* Alerte pour documents à signer */}
      {getUnsignedCount() > 0 && <div className="mb-6 bg-amber-50 border-l-4 border-amber-400 p-4 rounded-lg" data-id="element-301">
          <div className="flex items-start" data-id="element-302">
            <FileSignature className="h-5 w-5 text-amber-600 mt-0.5 mr-3" data-id="element-303" />
            <div data-id="element-304">
              <h3 className="text-sm font-medium text-amber-800" data-id="element-305">
                {getUnsignedCount()} document{getUnsignedCount() > 1 ? 's' : ''}{' '}
                en attente de signature
              </h3>
              <p className="text-sm text-amber-700 mt-1" data-id="element-306">
                Veuillez signer les documents requis pour compléter votre
                dossier.
              </p>
            </div>
          </div>
        </div>}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-307">
        {/* Left sidebar - Categories */}
        <div className="lg:col-span-1" data-id="element-308">
          <div className="bg-white rounded-lg shadow-sm p-5" data-id="element-309">
            <h2 className="font-semibold text-gray-900 mb-4" data-id="element-310">Catégories</h2>
            <div className="space-y-2" data-id="element-311">
              {documentCategories.map(category => <div key={category.id} className="flex justify-between items-center px-3 py-2 rounded-md hover:bg-gray-100 cursor-pointer" data-id="element-312">
                  <span className="text-gray-700" data-id="element-313">{category.name}</span>
                  <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full" data-id="element-314">
                    {category.count}
                  </span>
                </div>)}
            </div>
          </div>
        </div>

        {/* Main content - Documents */}
        <div className="lg:col-span-3" data-id="element-315">
          <div className="bg-white rounded-lg shadow-sm p-5 mb-6" data-id="element-316">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6" data-id="element-317">
              <div className="relative flex-grow" data-id="element-318">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-319">
                  <Search className="h-5 w-5 text-gray-400" data-id="element-320" />
                </div>
                <input type="text" placeholder="Rechercher un document..." className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-321" />
              </div>
              <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-322">
                <Filter className="h-4 w-4 mr-2" data-id="element-323" />
                Filtrer
              </button>
            </div>

            <h2 className="font-semibold text-gray-900 mb-4" data-id="element-324">
              Documents récents
            </h2>
            <div className="overflow-x-auto" data-id="element-325">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-326">
                <thead className="bg-gray-50" data-id="element-327">
                  <tr data-id="element-328">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-329">
                      Nom
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-330">
                      Catégorie
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-331">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-332">
                      Statut
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-333">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-334">
                  {recentDocuments.map(document => <tr key={document.id} className={document.requiresSignature && !document.signed ? 'bg-amber-50' : ''} data-id="element-335">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-336">
                        <div className="flex items-center" data-id="element-337">
                          <div className="flex-shrink-0 h-8 w-8 bg-gray-100 rounded-md flex items-center justify-center" data-id="element-338">
                            <FileText className="h-4 w-4 text-gray-500" data-id="element-339" />
                          </div>
                          <div className="ml-3" data-id="element-340">
                            <div className="text-sm font-medium text-gray-900" data-id="element-341">
                              {document.title}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-342">
                              {document.type.toUpperCase()}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-343">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800" data-id="element-344">
                          {document.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-345">
                        {document.date}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-346">
                        {document.requiresSignature ? document.signed ? <div className="flex items-center" data-id="element-347">
                              <CheckCircle className="h-4 w-4 text-green-600 mr-1" data-id="element-348" />
                              <span className="text-xs text-green-600 font-medium" data-id="element-349">
                                Signé
                              </span>
                            </div> : <div className="flex items-center" data-id="element-350">
                              <Clock className="h-4 w-4 text-amber-600 mr-1" data-id="element-351" />
                              <span className="text-xs text-amber-600 font-medium" data-id="element-352">
                                À signer
                              </span>
                            </div> : <span className="text-xs text-gray-500" data-id="element-353">-</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-354">
                        <div className="flex items-center justify-end gap-2" data-id="element-355">
                          <button className="text-blue-600 hover:text-blue-900" data-id="element-356">
                            Voir
                          </button>
                          <button className="text-blue-600 hover:text-blue-900" data-id="element-357">
                            <Download className="h-4 w-4" data-id="element-358" />
                          </button>
                          {document.requiresSignature && !document.signed && <button onClick={() => handleOpenSignModal(document)} className="px-3 py-1 bg-amber-600 text-white rounded text-xs hover:bg-amber-700 flex items-center gap-1" data-id="element-359">
                              <FileSignature className="h-3 w-3" data-id="element-360" />
                              Signer
                            </button>}
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-5" data-id="element-361">
            <h2 className="font-semibold text-gray-900 mb-4" data-id="element-362">
              Documents importants
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-363">
              <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 transition-colors duration-150 cursor-pointer" data-id="element-364">
                <div className="flex items-start" data-id="element-365">
                  <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-md flex items-center justify-center" data-id="element-366">
                    <FileText className="h-5 w-5 text-blue-600" data-id="element-367" />
                  </div>
                  <div className="ml-3" data-id="element-368">
                    <h3 className="text-sm font-medium text-gray-900" data-id="element-369">
                      Guide du nouvel employé
                    </h3>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-370">
                      Tout ce que vous devez savoir pour bien démarrer
                    </p>
                  </div>
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 transition-colors duration-150 cursor-pointer" data-id="element-371">
                <div className="flex items-start" data-id="element-372">
                  <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-md flex items-center justify-center" data-id="element-373">
                    <FileText className="h-5 w-5 text-blue-600" data-id="element-374" />
                  </div>
                  <div className="ml-3" data-id="element-375">
                    <h3 className="text-sm font-medium text-gray-900" data-id="element-376">
                      Politique de congés
                    </h3>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-377">
                      Règles et procédures pour les demandes de congés
                    </p>
                  </div>
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 transition-colors duration-150 cursor-pointer" data-id="element-378">
                <div className="flex items-start" data-id="element-379">
                  <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-md flex items-center justify-center" data-id="element-380">
                    <FileText className="h-5 w-5 text-blue-600" data-id="element-381" />
                  </div>
                  <div className="ml-3" data-id="element-382">
                    <h3 className="text-sm font-medium text-gray-900" data-id="element-383">
                      Guide des avantages sociaux
                    </h3>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-384">
                      Détails sur les avantages offerts aux employés
                    </p>
                  </div>
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 transition-colors duration-150 cursor-pointer" data-id="element-385">
                <div className="flex items-start" data-id="element-386">
                  <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-md flex items-center justify-center" data-id="element-387">
                    <FileText className="h-5 w-5 text-blue-600" data-id="element-388" />
                  </div>
                  <div className="ml-3" data-id="element-389">
                    <h3 className="text-sm font-medium text-gray-900" data-id="element-390">
                      Procédure d'évaluation
                    </h3>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-391">
                      Comment se déroulent les évaluations annuelles
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de signature */}
      <Modal isOpen={isSignModalOpen} onClose={() => {
      setIsSignModalOpen(false);
      setSelectedDocument(null);
    }} title="Signature de document" size="lg" data-id="element-392">
        {selectedDocument && <div className="space-y-6" data-id="element-393">
            {/* Informations du document */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-394">
              <div className="flex items-start gap-3" data-id="element-395">
                <div className="flex-shrink-0 h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-396">
                  <FileText className="h-6 w-6 text-blue-600" data-id="element-397" />
                </div>
                <div className="flex-1" data-id="element-398">
                  <h3 className="font-semibold text-gray-900 mb-1" data-id="element-399">
                    {selectedDocument.title}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600" data-id="element-400">
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-medium" data-id="element-401">
                      {selectedDocument.category}
                    </span>
                    <span data-id="element-402">•</span>
                    <span data-id="element-403">{selectedDocument.date}</span>
                    <span data-id="element-404">•</span>
                    <span className="uppercase" data-id="element-405">{selectedDocument.type}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Aperçu du document (simulé) */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 bg-gray-50" data-id="element-406">
              <div className="text-center text-gray-500" data-id="element-407">
                <FileText className="h-16 w-16 mx-auto mb-4 text-gray-400" data-id="element-408" />
                <p className="text-sm font-medium mb-2" data-id="element-409">Aperçu du document</p>
                <p className="text-xs" data-id="element-410">
                  Le contenu complet du document serait affiché ici
                </p>
              </div>
            </div>

            {/* Déclaration de signature */}
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg" data-id="element-411">
              <div className="flex items-start" data-id="element-412">
                <FileSignature className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" data-id="element-413" />
                <div data-id="element-414">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2" data-id="element-415">
                    Déclaration de signature électronique
                  </h4>
                  <p className="text-sm text-blue-800 leading-relaxed" data-id="element-416">
                    En cliquant sur "Je signe ce document", vous confirmez avoir
                    lu et compris le contenu de ce document. Votre signature
                    électronique aura la même valeur légale qu'une signature
                    manuscrite.
                  </p>
                  <p className="text-xs text-blue-700 mt-2" data-id="element-417">
                    Date et heure de signature :{' '}
                    {new Date().toLocaleString('fr-CA', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                  </p>
                </div>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t" data-id="element-418">
              <button onClick={() => {
            setIsSignModalOpen(false);
            setSelectedDocument(null);
          }} className="w-full sm:w-auto px-6 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-419">
                Annuler
              </button>
              <button onClick={handleSignDocument} className="w-full sm:w-auto px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium shadow-sm transition-colors flex items-center justify-center gap-2" data-id="element-420">
                <FileSignature className="h-4 w-4" data-id="element-421" />
                En cliquant je signe ce document
              </button>
            </div>
          </div>}
      </Modal>
    </div>;
}